import { StyleSheet, TextStyle } from 'react-native'

import { CSSObject } from 'styled-components'
import styled from 'styled-components/native'

import {
  FFM_COMPONENT_KEY,
  FFM_COMPONENT_PROP_KEY,
  FontFamilyMetadata,
  getFontFamilyFromMetadata,
  SUPPORTED_FONT_FAMILY,
  ThemeColors,
} from '@lyrahealth-inc/shared-app-logic'

import { IS_LIBRARY } from '../constants'

export enum TextType {
  HEADLINE = 'headline',
  SUBHEAD = 'subhead',
  BODY = 'body',
  CTA = 'cta',
}
export enum HeadlineSize {
  LARGE = 'large',
  MEDIUM = 'medium',
  SMALL = 'small',
  EYEBROW = 'eyebrow',
}
export enum SubheadSize {
  XLARGE = 'xlarge',
  LARGE = 'large',
  MEDIUM = 'medium',
  SMALL = 'small',
  XSMALL = 'xsmall',
}
export enum BodyTextSize {
  XLARGE = 'xlarge',
  MEDLARGE = 'medlarge', // Pending design approval
  LARGE = 'large',
  DEFAULT = 'default',
  SMALL = 'small',
  CAPTION = 'caption',
  BADGE = 'badge',
}
enum CTASize {
  BUTTON = 'button',
}

export type TextSize = HeadlineSize | SubheadSize | BodyTextSize | CTASize

export type TextAlign = 'left' | 'right' | 'center'

export type FontFamilyStyling = {
  fontFamily: string
  fontWeight?: string
}

// Font weights
const LIGHT = '300'
const REGULAR = '400'
const MEDIUM = '500'
const BOLD = '700'

// When exporting Ui-Core-Mobile components for the web we need to map the fonts to the declarations made in Ui-Core
export const moderatBold = IS_LIBRARY ? { fontFamily: 'moderat', fontWeight: BOLD } : { fontFamily: 'Moderat-Bold' }
export const moderatMedium = IS_LIBRARY
  ? { fontFamily: 'moderat', fontWeight: MEDIUM }
  : { fontFamily: 'Moderat-Medium' }
export const moderatRegular = IS_LIBRARY
  ? { fontFamily: 'moderat', fontWeight: REGULAR }
  : { fontFamily: 'Moderat-Regular' }
export const moderatLight = IS_LIBRARY ? { fontFamily: 'moderat', fontWeight: LIGHT } : { fontFamily: 'Moderat-Light' }
export const moderatPlain = { fontFamily: 'moderat' }

export const ppFragmentBold = IS_LIBRARY
  ? { fontFamily: 'PP Fragment', fontWeight: BOLD }
  : { fontFamily: 'PP Fragment-Bold' }
export const ppFragmentMedium = IS_LIBRARY
  ? { fontFamily: 'PP Fragment', fontWeight: MEDIUM }
  : { fontFamily: 'PP Fragment-Medium' }
export const ppFragmentRegular = IS_LIBRARY
  ? { fontFamily: 'PP Fragment', fontWeight: REGULAR }
  : { fontFamily: 'PP Fragment-Regular' }
export const ppFragmentPlain = { fontFamily: 'PP Fragment' }
export const ppFragmentLight = IS_LIBRARY
  ? { fontFamily: 'PP Fragment', fontWeight: LIGHT }
  : { fontFamily: 'PP Fragment-Light' }

export enum FONT_FAMILY_AND_WEIGHT {
  BOLD,
  MEDIUM,
  REGULAR,
  PLAIN,
  LIGHT,
}

export const getSupportedFontStyling = (
  fontFamily: SUPPORTED_FONT_FAMILY,
  fontFamilyAndWeightType: FONT_FAMILY_AND_WEIGHT,
): FontFamilyStyling => {
  if (fontFamily === SUPPORTED_FONT_FAMILY.PP_FRAGMENT) {
    switch (fontFamilyAndWeightType) {
      case FONT_FAMILY_AND_WEIGHT.BOLD:
        return ppFragmentBold
      case FONT_FAMILY_AND_WEIGHT.MEDIUM:
        return ppFragmentMedium
      case FONT_FAMILY_AND_WEIGHT.LIGHT:
        return ppFragmentLight
      case FONT_FAMILY_AND_WEIGHT.REGULAR:
        return ppFragmentRegular
      case FONT_FAMILY_AND_WEIGHT.PLAIN:
        return ppFragmentPlain
      default:
        return ppFragmentPlain
    }
  } else {
    switch (fontFamilyAndWeightType) {
      case FONT_FAMILY_AND_WEIGHT.BOLD:
        return moderatBold
      case FONT_FAMILY_AND_WEIGHT.MEDIUM:
        return moderatMedium
      case FONT_FAMILY_AND_WEIGHT.REGULAR:
        return moderatRegular
      case FONT_FAMILY_AND_WEIGHT.PLAIN:
        return moderatPlain
      case FONT_FAMILY_AND_WEIGHT.LIGHT:
        return moderatLight
      default:
        return moderatPlain
    }
  }
}

// Helper to determine body text weight - respects useRegularBodyWeight override for Aurora themes
const getBodyTextWeight = (fontFamilyMetadata: FontFamilyMetadata): FONT_FAMILY_AND_WEIGHT => {
  if (fontFamilyMetadata.isAuroraMetadata && fontFamilyMetadata.useRegularBodyWeight) {
    return FONT_FAMILY_AND_WEIGHT.REGULAR
  }
  return fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.REGULAR
}

export const getFontStyles = (colors: ThemeColors, fontFamilyMetadata: FontFamilyMetadata) => ({
  [TextType.HEADLINE]: {
    [HeadlineSize.LARGE]: {
      ...getSupportedFontStyling(
        getFontFamilyFromMetadata(fontFamilyMetadata, FFM_COMPONENT_KEY.BASE_TEXT, FFM_COMPONENT_PROP_KEY.HEADLINE),
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.BOLD,
      ),
      fontSize: 56,
      lineHeight: 66,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [HeadlineSize.MEDIUM]: {
      ...getSupportedFontStyling(
        getFontFamilyFromMetadata(fontFamilyMetadata, FFM_COMPONENT_KEY.BASE_TEXT, FFM_COMPONENT_PROP_KEY.HEADLINE),
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.BOLD,
      ),
      fontSize: 48,
      lineHeight: 54,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [HeadlineSize.SMALL]: {
      ...getSupportedFontStyling(
        getFontFamilyFromMetadata(fontFamilyMetadata, FFM_COMPONENT_KEY.BASE_TEXT, FFM_COMPONENT_PROP_KEY.HEADLINE),
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.BOLD,
      ),
      fontSize: 34,
      lineHeight: 38,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [HeadlineSize.EYEBROW]: {
      ...getSupportedFontStyling(
        SUPPORTED_FONT_FAMILY.MODERAT,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.MEDIUM : FONT_FAMILY_AND_WEIGHT.BOLD,
      ),
      fontSize: 12,
      lineHeight: 18,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: colors.textPrimary,
    } as CSSObject | TextStyle,
  },
  [TextType.SUBHEAD]: {
    [SubheadSize.XLARGE]: {
      ...getSupportedFontStyling(
        getFontFamilyFromMetadata(fontFamilyMetadata, FFM_COMPONENT_KEY.BASE_TEXT, FFM_COMPONENT_PROP_KEY.SUBHEAD),
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.MEDIUM,
      ),
      fontSize: 34,
      lineHeight: 40.8,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [SubheadSize.LARGE]: {
      ...getSupportedFontStyling(
        getFontFamilyFromMetadata(fontFamilyMetadata, FFM_COMPONENT_KEY.BASE_TEXT, FFM_COMPONENT_PROP_KEY.SUBHEAD),
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.MEDIUM,
      ),
      fontSize: 28,
      lineHeight: 34,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [SubheadSize.MEDIUM]: {
      ...getSupportedFontStyling(
        getFontFamilyFromMetadata(fontFamilyMetadata, FFM_COMPONENT_KEY.BASE_TEXT, FFM_COMPONENT_PROP_KEY.SUBHEAD),
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.MEDIUM,
      ),
      fontSize: 22,
      lineHeight: 28,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [SubheadSize.SMALL]: {
      ...getSupportedFontStyling(
        getFontFamilyFromMetadata(fontFamilyMetadata, FFM_COMPONENT_KEY.BASE_TEXT, FFM_COMPONENT_PROP_KEY.SUBHEAD),
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.MEDIUM,
      ),
      fontSize: 18,
      lineHeight: 24,
      textTransform: 'none',
      color: colors.textPrimary,
    },
    [SubheadSize.XSMALL]: {
      ...getSupportedFontStyling(
        getFontFamilyFromMetadata(fontFamilyMetadata, FFM_COMPONENT_KEY.BASE_TEXT, FFM_COMPONENT_PROP_KEY.SUBHEAD),
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.LIGHT : FONT_FAMILY_AND_WEIGHT.MEDIUM,
      ),
      fontSize: 16,
      lineHeight: 22,
      textTransform: 'none',
      color: colors.textPrimary,
    },
  },
  [TextType.BODY]: {
    [BodyTextSize.XLARGE]: {
      ...getSupportedFontStyling(SUPPORTED_FONT_FAMILY.MODERAT, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 34,
      lineHeight: 28,
      color: colors.textPrimary,
    },
    [BodyTextSize.MEDLARGE]: {
      ...getSupportedFontStyling(SUPPORTED_FONT_FAMILY.MODERAT, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 28,
      lineHeight: 39,
      color: colors.textPrimary,
    },
    [BodyTextSize.LARGE]: {
      ...getSupportedFontStyling(SUPPORTED_FONT_FAMILY.MODERAT, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 18,
      lineHeight: 28,
      color: colors.textPrimary,
    },
    [BodyTextSize.DEFAULT]: {
      ...getSupportedFontStyling(SUPPORTED_FONT_FAMILY.MODERAT, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 16,
      lineHeight: 24,
      color: colors.textPrimary,
    },
    [BodyTextSize.SMALL]: {
      ...getSupportedFontStyling(SUPPORTED_FONT_FAMILY.MODERAT, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 14,
      lineHeight: 20,
      color: colors.textPrimary,
    },
    [BodyTextSize.CAPTION]: {
      ...getSupportedFontStyling(SUPPORTED_FONT_FAMILY.MODERAT, getBodyTextWeight(fontFamilyMetadata)),
      fontSize: 12,
      lineHeight: 18,
      color: colors.textPrimary,
    },
    [BodyTextSize.BADGE]: {
      ...getSupportedFontStyling(
        SUPPORTED_FONT_FAMILY.MODERAT,
        fontFamilyMetadata.isAuroraMetadata ? FONT_FAMILY_AND_WEIGHT.REGULAR : FONT_FAMILY_AND_WEIGHT.BOLD,
      ),
      fontSize: 12,
      lineHeight: 18,
      color: colors.textPrimary,
    },
  },
  [TextType.CTA]: {
    [CTASize.BUTTON]: {
      ...getSupportedFontStyling(SUPPORTED_FONT_FAMILY.MODERAT, FONT_FAMILY_AND_WEIGHT.MEDIUM),
      fontSize: 16,
      lineHeight: 24,
    },
  },
})

export const getTypeStyles = (colors: ThemeColors, fontFamilyMetadata: FontFamilyMetadata) => {
  const fontStyles = getFontStyles(colors, fontFamilyMetadata)
  return StyleSheet.create({
    headlineLarge: fontStyles.headline.large,
    headlineMedium: fontStyles.headline.medium,
    headlineSmall: fontStyles.headline.small,
    subheadLarge: fontStyles.subhead.large,
    subheadMedium: fontStyles.subhead.medium,
    subheadSmall: fontStyles.subhead.small,
    subheadXsmall: fontStyles.subhead.xsmall,
    headlineEyebrow: fontStyles.headline.eyebrow,
    bodyLarge: fontStyles.body.large,
    bodyDefault: fontStyles.body.default,
    bodySmall: fontStyles.body.small,
    bodyCaption: fontStyles.body.caption,
    ctaButton: fontStyles.cta.button,
    em: { fontStyle: 'italic' },
  } as { [key: string]: TextStyle })
}

// This component is allowed to use styled.Text since it is the root component for all Text in the mobile app
// eslint-disable-next-line lyrahealth/no-react-native-text
export const StyledText = styled.Text<{ type: TextType; size: TextSize }>(({ theme, type, size }) => {
  const style = getFontStyles(theme.colors, theme.fontFamilyMetadata)?.[type]?.[size]
  if (style) {
    const styleWithUnits = { ...style, lineHeight: `${style.lineHeight}px` }
    return styleWithUnits as CSSObject
  } else {
    console.warn(`StyledText: The type style ${type} ${size} does not exist`)
    return {} as CSSObject
  }
})

export const getHighlightedText = (colors: ThemeColors) => ({ color: colors.textActive, fontStyle: 'normal' })
